import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit, 
  startAfter,
  writeBatch,
  serverTimestamp 
} from 'firebase/firestore';
import { database as db } from '../config/firebase';
import { Quote, ContentCategory } from '@brovicms/models';
import toast from 'react-hot-toast';

export interface ServiceResult<T> {
  success: boolean;
  data?: T;
  error?: string;
  id?: string;
}

export interface ListResult<T> {
  data: T[];
  hasMore: boolean;
  lastDoc?: any;
}

export interface QuoteFormData {
  content: string;
  source?: string;
  bookReference?: {
    bookId: string;
    bookTitle: string;
    pageNumber?: number;
    chapter?: string;
  };
  category: ContentCategory;
  tags: string[];
  language: string;
  isPublished: boolean;
  isFeatured: boolean;
  isPremium: boolean;
}

class QuoteService {
  private collectionName = 'quotes';

  async createQuote(quoteData: QuoteFormData): Promise<ServiceResult<Quote>> {
    try {
      const docRef = await addDoc(collection(db, this.collectionName), {
        ...quoteData,
        type: 'quote',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        stats: {
          views: 0,
          downloads: 0,
          shares: 0,
          likes: 0,
          bookmarks: 0,
          averageRating: 0,
          totalRatings: 0,
          comments: 0
        },
        metadata: {
          seo: {
            title: quoteData.content.substring(0, 60),
            description: quoteData.content.substring(0, 160),
            keywords: quoteData.tags
          }
        }
      });

      const createdQuote = await this.getQuote(docRef.id);
      return {
        success: true,
        data: createdQuote.data,
        id: docRef.id
      };
    } catch (error: any) {
      console.error('Create quote error:', error);
      toast.error(`Failed to create quote: ${error.message}`);
      return {
        success: false,
        error: error.message
      };
    }
  }

  async getQuote(id: string): Promise<ServiceResult<Quote>> {
    try {
      const docRef = doc(db, this.collectionName, id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data() as Quote;
        return {
          success: true,
          data: {
            ...data,
            id: docSnap.id,
            createdAt: data.createdAt instanceof Date ? data.createdAt : (data.createdAt as any)?.toDate?.() || new Date(),
            updatedAt: data.updatedAt instanceof Date ? data.updatedAt : (data.updatedAt as any)?.toDate?.() || new Date(),
            publishedAt: data.publishedAt instanceof Date ? data.publishedAt : (data.publishedAt as any)?.toDate?.() || undefined
          }
        };
      } else {
        throw new Error('Quote not found');
      }
    } catch (error: any) {
      console.error('Get quote error:', error);
      toast.error(`Failed to get quote: ${error.message}`);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Remove undefined values from an object recursively
   * Firestore doesn't accept undefined values
   */
  private removeUndefinedValues(obj: any): any {
    if (obj === null || obj === undefined) {
      return null;
    }
    
    if (Array.isArray(obj)) {
      return obj.map(item => this.removeUndefinedValues(item));
    }
    
    if (typeof obj === 'object') {
      const cleaned: any = {};
      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          const value = obj[key];
          if (value !== undefined) {
            cleaned[key] = this.removeUndefinedValues(value);
          }
        }
      }
      return cleaned;
    }
    
    return obj;
  }

  async updateQuote(id: string, quoteData: Partial<QuoteFormData>): Promise<ServiceResult<Quote>> {
    try {
      const docRef = doc(db, this.collectionName, id);
      
      // Remove undefined values before updating
      const cleanedData = this.removeUndefinedValues(quoteData);
      
      await updateDoc(docRef, {
        ...cleanedData,
        updatedAt: serverTimestamp()
      });

      const updatedQuote = await this.getQuote(id);
      toast.success('Quote updated successfully');
      return {
        success: true,
        data: updatedQuote.data,
        id
      };
    } catch (error: any) {
      console.error('Update quote error:', error);
      toast.error(`Failed to update quote: ${error.message}`);
      return {
        success: false,
        error: error.message
      };
    }
  }

  async deleteQuote(id: string): Promise<ServiceResult<void>> {
    try {
      const docRef = doc(db, this.collectionName, id);
      await deleteDoc(docRef);
      
      toast.success('Quote deleted successfully');
      return {
        success: true,
        id
      };
    } catch (error: any) {
      console.error('Delete quote error:', error);
      toast.error(`Failed to delete quote: ${error.message}`);
      return {
        success: false,
        error: error.message
      };
    }
  }

  async getQuotes(options: { 
    limit?: number; 
    publishedOnly?: boolean;
    featuredOnly?: boolean;
    category?: string;
    search?: string;
  } = {}): Promise<ListResult<Quote>> {
    try {
      let q = query(collection(db, this.collectionName));

      if (options.publishedOnly) {
        q = query(q, where('isPublished', '==', true));
      }

      if (options.featuredOnly) {
        q = query(q, where('isFeatured', '==', true));
      }

      if (options.category) {
        q = query(q, where('category.id', '==', options.category));
      }

      if (options.search) {
        q = query(q, where('title', '>=', options.search), where('title', '<=', options.search + '\uf8ff'));
      }

      q = query(q, orderBy('createdAt', 'desc'));

      if (options.limit) {
        q = query(q, limit(options.limit));
      }

      const querySnapshot = await getDocs(q);
      const quotes: Quote[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data() as Quote;
        quotes.push({
          ...data,
          id: doc.id,
          createdAt: data.createdAt instanceof Date ? data.createdAt : (data.createdAt as any)?.toDate?.() || new Date(),
          updatedAt: data.updatedAt instanceof Date ? data.updatedAt : (data.updatedAt as any)?.toDate?.() || new Date(),
          publishedAt: data.publishedAt instanceof Date ? data.publishedAt : (data.publishedAt as any)?.toDate?.() || undefined
        });
      });

      return {
        data: quotes,
        hasMore: querySnapshot.docs.length === (options.limit || 10)
      };
    } catch (error: any) {
      console.error('Get quotes error:', error);
      toast.error(`Failed to get quotes: ${error.message}`);
      return {
        data: [],
        hasMore: false
      };
    }
  }

  async searchQuotes(searchTerm: string, options: { limit?: number } = {}): Promise<ListResult<Quote>> {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('title', '>=', searchTerm),
        where('title', '<=', searchTerm + '\uf8ff'),
        orderBy('title'),
        limit(options.limit || 10)
      );

      const querySnapshot = await getDocs(q);
      const quotes: Quote[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data() as Quote;
        quotes.push({
          ...data,
          id: doc.id,
          createdAt: data.createdAt instanceof Date ? data.createdAt : (data.createdAt as any)?.toDate?.() || new Date(),
          updatedAt: data.updatedAt instanceof Date ? data.updatedAt : (data.updatedAt as any)?.toDate?.() || new Date(),
          publishedAt: data.publishedAt instanceof Date ? data.publishedAt : (data.publishedAt as any)?.toDate?.() || undefined
        });
      });

      return {
        data: quotes,
        hasMore: querySnapshot.docs.length === (options.limit || 10)
      };
    } catch (error: any) {
      console.error('Search quotes error:', error);
      toast.error(`Failed to search quotes: ${error.message}`);
      return {
        data: [],
        hasMore: false
      };
    }
  }

  async getFeaturedQuotes(limitCount: number = 5): Promise<ListResult<Quote>> {
    return this.getQuotes({ 
      limit: limitCount, 
      publishedOnly: true, 
      featuredOnly: true 
    });
  }

  async getQuotesByCategory(categoryId: string, limitCount: number = 10): Promise<ListResult<Quote>> {
    return this.getQuotes({ 
      limit: limitCount, 
      publishedOnly: true, 
      category: categoryId 
    });
  }

  async publishQuote(id: string): Promise<ServiceResult<Quote>> {
    try {
      const docRef = doc(db, this.collectionName, id);
      await updateDoc(docRef, {
        isPublished: true,
        publishedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      const publishedQuote = await this.getQuote(id);
      toast.success('Quote published successfully');
      return {
        success: true,
        data: publishedQuote.data,
        id
      };
    } catch (error: any) {
      console.error('Publish quote error:', error);
      toast.error(`Failed to publish quote: ${error.message}`);
      return {
        success: false,
        error: error.message
      };
    }
  }

  async unpublishQuote(id: string): Promise<ServiceResult<Quote>> {
    try {
      const docRef = doc(db, this.collectionName, id);
      await updateDoc(docRef, {
        isPublished: false,
        publishedAt: null,
        updatedAt: serverTimestamp()
      });

      const unpublishedQuote = await this.getQuote(id);
      toast.success('Quote unpublished successfully');
      return {
        success: true,
        data: unpublishedQuote.data,
        id
      };
    } catch (error: any) {
      console.error('Unpublish quote error:', error);
      toast.error(`Failed to unpublish quote: ${error.message}`);
      return {
        success: false,
        error: error.message
      };
    }
  }

  async toggleFeatured(id: string, isFeatured: boolean): Promise<ServiceResult<Quote>> {
    try {
      const docRef = doc(db, this.collectionName, id);
      await updateDoc(docRef, {
        isFeatured,
        updatedAt: serverTimestamp()
      });

      const updatedQuote = await this.getQuote(id);
      toast.success(`Quote ${isFeatured ? 'featured' : 'unfeatured'} successfully`);
      return {
        success: true,
        data: updatedQuote.data,
        id
      };
    } catch (error: any) {
      console.error('Toggle featured error:', error);
      toast.error(`Failed to toggle featured status: ${error.message}`);
      return {
        success: false,
        error: error.message
      };
    }
  }

  async deleteAllQuotes(): Promise<ServiceResult<void>> {
    try {
      const quotes = await this.getQuotes();
      const batch = writeBatch(db);

      quotes.data.forEach((quote) => {
        const docRef = doc(db, this.collectionName, quote.id);
        batch.delete(docRef);
      });

      await batch.commit();
      toast.success('All quotes deleted successfully');
      return {
        success: true
      };
    } catch (error: any) {
      console.error('Delete all quotes error:', error);
      toast.error(`Failed to delete all quotes: ${error.message}`);
      return {
        success: false,
        error: error.message
      };
    }
  }
}

export const quoteService = new QuoteService();
